// vue-i18n setup. Persists the chosen locale in localStorage so users keep
// their language across reloads. Naive UI's own locale + dateLocale are wired
// to the same source in App.vue so date pickers and pagination follow suit.
import { createI18n } from 'vue-i18n'
import { nb as dateNb, enUS as dateEn } from 'date-fns/locale'
import { dateNbNO, dateEnUS, nbNO as naiveNb, enUS as naiveEn } from 'naive-ui'
import type { NDateLocale, NLocale } from 'naive-ui'
import type { Locale as DateFnsLocale } from 'date-fns'
import nb from './messages/nb.json'
import en from './messages/en.json'

export const SUPPORTED_LOCALES = ['nb', 'en'] as const
export type AppLocale = (typeof SUPPORTED_LOCALES)[number]

const STORAGE_KEY = 'storypoint.locale'

function loadInitialLocale(): AppLocale {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as AppLocale
    }
  }
  // Browser hint: anyone whose browser is Norwegian gets Norwegian by default,
  // everyone else gets English. Falls through to English on SSR / undefined.
  if (typeof navigator !== 'undefined') {
    const tag = navigator.language?.toLowerCase() ?? ''
    if (tag.startsWith('nb') || tag.startsWith('nn') || tag.startsWith('no')) return 'nb'
  }
  return 'en'
}

export const i18n = createI18n({
  legacy: false,
  locale: loadInitialLocale(),
  fallbackLocale: 'nb',
  messages: { nb, en },
  // Norwegian formatting for both locales — the app is Norway-focused and
  // mixing US-style numbers in an English UI for a Norwegian product reads
  // worse than keeping the regional convention.
  numberFormats: {
    nb: { decimal: { style: 'decimal' } },
    en: { decimal: { style: 'decimal' } },
  },
})

interface LocaleBundle {
  naive: NLocale
  naiveDate: NDateLocale
  dateFns: DateFnsLocale
  htmlLang: string
}

const BUNDLES: Record<AppLocale, LocaleBundle> = {
  nb: { naive: naiveNb, naiveDate: dateNbNO, dateFns: dateNb, htmlLang: 'nb' },
  en: { naive: naiveEn, naiveDate: dateEnUS, dateFns: dateEn, htmlLang: 'en' },
}

export function localeBundle(locale: AppLocale): LocaleBundle {
  return BUNDLES[locale]
}

export function setLocale(locale: AppLocale) {
  i18n.global.locale.value = locale
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, locale)
  }
  if (typeof document !== 'undefined') {
    document.documentElement.lang = BUNDLES[locale].htmlLang
  }
}
